// The tenant edits on S5 (spec 15 §4, §5; M15-6): rename and defaults through
// PATCH /v1/platform/tenants/:id, and the subdomain move through
// POST …/subdomain. platform_admin only, and a sandbox member on a fleet
// tenant is offered neither (D-477). The currency is never editable here:
// it is locked once money-bearing rows exist (spec 16 §2.1).

import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { Field } from "../../../components/field";
import { Modal } from "../../../components/modal";
import { Select } from "../../../components/select";
import { Banner } from "../../../components/toast";
import { LOCALE_ENDONYMS } from "../../../lib/locale";
import { useHeldKey, withHeldKey } from "../lib/held-key";
import { PlatformError, type TenantDetail } from "../lib/platform-api";
import { renameSubdomain, updateTenant } from "../lib/plans-api";
import { TENANT_LOCALES } from "../lib/tenants";
import { useSignInAgainOn401 } from "./console-frame";

const SUBDOMAIN = /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/;

function zones(current: string): readonly string[] {
  const list =
    (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.(
      "timeZone",
    ) ?? [];
  return list.includes(current) ? list : [current, ...list];
}

export function EditTenantDialog({
  detail,
  onClose,
  onDone,
}: {
  detail: TenantDetail;
  onClose: () => void;
  onDone: (next: TenantDetail) => void;
}) {
  const { t } = useTranslation();
  const heldKey = useHeldKey();
  const tenant = detail.tenant;
  const zoneList = useMemo(() => zones(tenant.default_timezone), [tenant.default_timezone]);
  const [name, setName] = useState(tenant.name);
  const [locale, setLocale] = useState(tenant.default_locale);
  const [zone, setZone] = useState(tenant.default_timezone);
  const body = {
    ...(name.trim() !== tenant.name ? { name: name.trim() } : {}),
    ...(locale !== tenant.default_locale ? { default_locale: locale } : {}),
    ...(zone !== tenant.default_timezone ? { default_timezone: zone } : {}),
  };
  const changed = Object.keys(body).length > 0 && name.trim() !== "";
  const save = useMutation({
    mutationFn: () =>
      withHeldKey(heldKey, { edit: tenant.id, body }, (key) => updateTenant(tenant.id, body, key)),
    onSuccess: onDone,
  });
  useSignInAgainOn401(save.error);
  const title = t("admin.tenantEdit.title", { name: tenant.name });
  return (
    <Modal
      label={title}
      heading={title}
      closeLabel={t("admin.plans.close")}
      onClose={onClose}
      pending={save.isPending}
      testId="admin-tenant-edit-dialog"
      className="admin-modal"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (changed) save.mutate();
        }}
      >
        <Field label={t("admin.tenantEdit.name")} wide>
          <input
            className="ds-input"
            value={name}
            maxLength={200}
            data-testid="admin-tenant-edit-name"
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </Field>
        <Field label={t("admin.tenantEdit.locale")}>
          <Select
            value={locale}
            data-testid="admin-tenant-edit-locale"
            onChange={(e) => {
              setLocale(e.target.value as typeof locale);
            }}
          >
            {TENANT_LOCALES.map((l) => (
              <option key={l} value={l}>
                {LOCALE_ENDONYMS[l]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("admin.tenantEdit.timezone")}>
          <Select
            value={zone}
            data-testid="admin-tenant-edit-timezone"
            onChange={(e) => {
              setZone(e.target.value);
            }}
          >
            {zoneList.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </Select>
        </Field>
        <p className="admin-hint">{t("admin.tenantEdit.currencyLocked")}</p>
        {save.isError ? (
          <Banner kind="bad" data-testid="admin-tenant-edit-error">
            {save.error instanceof PlatformError && save.error.status === 403
              ? t("admin.tenant.refusedRole")
              : t("admin.tenantEdit.failed")}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={!changed}
            busy={save.isPending}
            busyLabel={t("admin.plans.saving")}
            data-testid="admin-tenant-edit-save"
          >
            {t("admin.tenantEdit.save")}
          </Button>
          <Button disabled={save.isPending} onClick={onClose}>
            {t("admin.plans.cancel")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function SubdomainDialog({
  detail,
  hostFor,
  onClose,
  onDone,
}: {
  detail: TenantDetail;
  hostFor: (subdomain: string) => string;
  onClose: () => void;
  onDone: (next: TenantDetail) => void;
}) {
  const { t } = useTranslation();
  const heldKey = useHeldKey();
  const queryClient = useQueryClient();
  const tenant = detail.tenant;
  const [next, setNext] = useState("");
  const [reason, setReason] = useState("");
  const sub = next.trim().toLowerCase();
  const ok = SUBDOMAIN.test(sub) && sub !== tenant.subdomain && reason.trim().length >= 5;
  const move = useMutation({
    mutationFn: () => {
      const body = { new_subdomain: sub, reason: reason.trim() };
      return withHeldKey(heldKey, { move: tenant.id, body }, (key) =>
        renameSubdomain(tenant.id, body, key),
      );
    },
    onSuccess: (detailNext) => {
      void queryClient.invalidateQueries({ queryKey: ["platform", "subdomain-check"] });
      onDone(detailNext);
    },
  });
  useSignInAgainOn401(move.error);
  const code = move.error instanceof PlatformError ? move.error.code : undefined;
  const title = t("admin.tenantEdit.subdomainTitle", { name: tenant.name });
  return (
    <Modal
      label={title}
      heading={title}
      closeLabel={t("admin.plans.close")}
      onClose={onClose}
      pending={move.isPending}
      testId="admin-subdomain-dialog"
      className="admin-modal"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) move.mutate();
        }}
      >
        <Field label={t("admin.tenantEdit.newSubdomain")} wide>
          <input
            className="ds-input"
            value={next}
            maxLength={63}
            spellCheck={false}
            data-testid="admin-subdomain-input"
            onChange={(e) => {
              setNext(e.target.value);
            }}
          />
        </Field>
        <p className="admin-hint">
          {SUBDOMAIN.test(sub)
            ? t("admin.tenantEdit.subdomainPreview", { host: hostFor(sub) })
            : t("admin.tenantEdit.subdomainRule")}
        </p>
        <Field label={t("admin.tenantEdit.reason")} wide>
          <textarea
            className="ds-input"
            rows={2}
            maxLength={2000}
            value={reason}
            data-testid="admin-subdomain-reason"
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </Field>
        <p className="admin-hint">{t("admin.tenantEdit.subdomainHint")}</p>
        {move.isError ? (
          <Banner kind="bad" data-testid="admin-subdomain-error">
            {code === "SUBDOMAIN_TAKEN"
              ? t("admin.tenantEdit.subdomainTaken")
              : code === "SUBDOMAIN_RESERVED"
                ? t("admin.tenantEdit.subdomainReserved")
                : move.error instanceof PlatformError && move.error.status === 403
                  ? t("admin.tenant.refusedRole")
                  : t("admin.tenantEdit.subdomainFailed")}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={!ok}
            busy={move.isPending}
            busyLabel={t("admin.plans.saving")}
            data-testid="admin-subdomain-save"
          >
            {t("admin.tenantEdit.subdomainSave")}
          </Button>
          <Button disabled={move.isPending} onClick={onClose}>
            {t("admin.plans.cancel")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
